/**
 * Waiver-lifecycle chip picker for the BPA violations table (Slice U-F2).
 *
 * Each row in the violations table can optionally render a small chip beside
 * its status badge so the user understands *why* a row looks the way it does
 * without cross-referencing the moves feed. Four states only:
 *
 *   - `expiring-soon` — Waived row, <=14 days until expiry, not yet expired
 *   - `expired`       — Waived row, ExpiresAt is in the past (grace window
 *                       before ProcessExpiredWaivers sweeps the row back to
 *                       Open). Matches the existing "Waiver expired" rose-
 *                       tokens styling on the moves feed.
 *   - `lapsed`        — Open row, a waiver on this policy lapsed within the
 *                       last 30 days (RecentLapsedAt populated by the backend).
 *   - `none`          — anything else, including Permanent waivers (no
 *                       ExpiresAt by design).
 *
 * The chip-picker is a pure function over a slim subset of the violation
 * shape so it stays unit-testable without pulling in the full
 * ComplianceViolation type or React.
 */

export type WaiverLifecycleChipKind =
  | 'none'
  | 'expiring-soon'
  | 'expired'
  | 'lapsed'

export interface WaiverLifecycleChip {
  kind: Exclude<WaiverLifecycleChipKind, 'none'>
  /** Pre-composed label, e.g. "Expires in 3 days", "Waiver expired",
   *  "Waiver lapsed May 18". The renderer just drops this into a span. */
  label: string
  /** Tooltip text. Mirrors editorial voice — short, advisor framing. */
  tooltip: string
}

/**
 * Minimal slice of a violation row the chip-picker needs. Kept narrow on
 * purpose: callers can pass either a ViolationSummary or full
 * ComplianceViolation without a cast, and the test fixture stays tiny.
 */
export interface WaiverChipInput {
  status: string
  waiver?: {
    waiverType?: string
    expiresAt?: string
    isExpired?: boolean
  } | null
  recentLapsedAt?: string | null
}

/**
 * Pick the chip (if any) to render for this violation row.
 *
 * `now` is injected for deterministic tests; callers in app code can omit
 * it and the helper will read the wall clock. All comparisons are in UTC —
 * the chip text uses the existing date formatter (which renders local) so
 * the comparison-vs-render distinction is intentional.
 */
export function pickWaiverLifecycleChip(
  v: WaiverChipInput,
  now: Date = new Date(),
): WaiverLifecycleChip | null {
  // Waived rows: surface expiry chips, but only for Temporary waivers.
  // Permanent waivers have no ExpiresAt by design and Not-Applicable
  // designations also carry no expiry — neither should render a chip.
  if (v.status === 'Waived' && v.waiver) {
    // Permanent / Not Applicable have no expiry — no chip.
    if (!v.waiver.expiresAt) return null

    if (v.waiver.isExpired === true) {
      // Past expiry but not yet swept by ProcessExpiredWaivers. Matches the
      // "Waiver expired" pill on the BPA moves feed for visual consistency.
      return {
        kind: 'expired',
        label: 'Waiver expired',
        tooltip:
          'The waiver has lapsed. This row flips back to Open at the next overnight sweep, or remove the waiver to flip it now.',
      }
    }

    const expires = parseIsoSafe(v.waiver.expiresAt)
    if (!expires) return null
    const days = daysBetween(now, expires)

    // Outside the 14-day window we stay quiet — the live "Temporary
    // Exception | expires May 24" label already on the row carries the
    // date for users who care.
    if (days > 14 || days < 0) return null

    return {
      kind: 'expiring-soon',
      label: formatExpiresLabel(days, expires),
      tooltip:
        'This temporary waiver lapses soon. Renew or replace it before the expiry date to avoid the row returning to Open.',
    }
  }

  // Open / Resolved / DataUnavailable rows: surface a recent-lapsed chip if
  // the backend tagged one. Backend only populates RecentLapsedAt when the
  // row is NOT currently Waived, so the status guard here is belt + braces.
  if (v.status !== 'Waived' && v.recentLapsedAt) {
    const lapsed = parseIsoSafe(v.recentLapsedAt)
    if (!lapsed) return null
    return {
      kind: 'lapsed',
      label: `Waiver lapsed ${formatShort(lapsed)}`,
      tooltip:
        'A waiver on this policy lapsed recently. Re-evaluate whether the underlying condition still warrants a waiver, or remediate.',
    }
  }

  return null
}

// ── helpers ────────────────────────────────────────────────────────────────

function parseIsoSafe(s: string): Date | null {
  // Date-only ISO (YYYY-MM-DD) — anchor at local-noon so timezone math
  // doesn't shift the calendar date out from under the user. Full ISO
  // strings carry their own zone info; just parse.
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (dateOnlyMatch) {
    const y = Number(dateOnlyMatch[1])
    const m = Number(dateOnlyMatch[2])
    const d = Number(dateOnlyMatch[3])
    const parsed = new Date(y, m - 1, d, 12, 0, 0)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const parsed = new Date(s)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function daysBetween(now: Date, future: Date): number {
  // Floor against the day boundary so "expires today" reads as 0, and
  // "expires tomorrow" reads as 1. Avoids the off-by-one users notice
  // when a row says "Expires in 14 days" then doesn't disappear at the
  // 14-day mark.
  const ms = future.getTime() - now.getTime()
  return Math.floor(ms / (1000 * 60 * 60 * 24))
}

function formatShort(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatExpiresLabel(days: number, expires: Date): string {
  // Compact-relative for the imminent window (where urgency matters) and
  // absolute-date for the longer window (where the calendar reference is
  // more actionable). Threshold of 7 days picked to match the typical
  // "this-week vs next-week" mental model.
  if (days <= 0) return 'Expires today'
  if (days === 1) return 'Expires in 1 day'
  if (days <= 7) return `Expires in ${days} days`
  return `Expires ${formatShort(expires)}`
}
